import React, { useMemo } from "react";
import { Modal, Typography } from "antd";
import { PendingAddition } from "./SchemaTreeEditor";
import { generateFileChanges } from "./SchemaGitService";

const { Text } = Typography;

interface SchemaDiffPreviewProps {
  additions: PendingAddition[];
  fileContents: Record<string, string>;
  onConfirm: () => void;
  onCancel: () => void;
}

interface DiffLine {
  type: "added" | "removed" | "context";
  text: string;
  /** Unique key for React rendering, assigned during diff computation */
  key: string;
}

/**
 * Computes a simple unified diff between two strings.
 * Shows context lines, removed lines (red), and added lines (green).
 */
function computeSimpleDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split("\n");
  const newLines = newText.split("\n");
  const result: DiffLine[] = [];
  let lineCounter = 0;

  function pushLine(type: DiffLine["type"], text: string) {
    lineCounter += 1;
    result.push({ type, text, key: `${type}-${lineCounter}` });
  }

  let oldIdx = 0;
  let newIdx = 0;

  while (oldIdx < oldLines.length && newIdx < newLines.length) {
    if (oldLines[oldIdx] === newLines[newIdx]) {
      pushLine("context", oldLines[oldIdx]);
      oldIdx += 1;
      newIdx += 1;
    } else {
      // Capture current indices so closures don't reference mutable loop vars
      const currentOldIdx = oldIdx;
      const currentNewIdx = newIdx;

      // Find how many old lines are different before re-sync
      const remainingOld = oldLines.slice(currentOldIdx);
      const remainingNew = newLines.slice(currentNewIdx);

      const oldLookAhead = remainingOld.findIndex(
        (line, i) => i > 0 && remainingNew.some((nl) => nl === line),
      );
      const newLookAhead = remainingNew.findIndex(
        (line, i) => i > 0 && remainingOld.some((ol) => ol === line),
      );

      if (oldLookAhead > 0 && (newLookAhead <= 0 || oldLookAhead <= newLookAhead)) {
        // Old lines were removed
        for (let i = 0; i < oldLookAhead; i += 1) {
          pushLine("removed", oldLines[oldIdx]);
          oldIdx += 1;
        }
      } else if (newLookAhead > 0) {
        // New lines were added
        for (let i = 0; i < newLookAhead; i += 1) {
          pushLine("added", newLines[newIdx]);
          newIdx += 1;
        }
      } else {
        // No re-sync found — treat remaining as removals + additions
        pushLine("removed", oldLines[oldIdx]);
        pushLine("added", newLines[newIdx]);
        oldIdx += 1;
        newIdx += 1;
      }
    }
  }

  // Remaining old lines are removals
  while (oldIdx < oldLines.length) {
    pushLine("removed", oldLines[oldIdx]);
    oldIdx += 1;
  }

  // Remaining new lines are additions
  while (newIdx < newLines.length) {
    pushLine("added", newLines[newIdx]);
    newIdx += 1;
  }

  return result;
}

function getDiffLineStyle(type: "added" | "removed" | "context"): React.CSSProperties {
  if (type === "added") {
    return { backgroundColor: "#e6ffed", color: "#22863a" };
  }
  if (type === "removed") {
    return { backgroundColor: "#ffeef0", color: "#cb2431" };
  }
  return {};
}

function getDiffLinePrefix(type: "added" | "removed" | "context"): string {
  if (type === "added") return "+";
  if (type === "removed") return "-";
  return " ";
}

function SchemaDiffPreview({
  additions,
  fileContents,
  onConfirm,
  onCancel,
}: SchemaDiffPreviewProps) {
  const fileDiffs = useMemo(() => {
    const changes = generateFileChanges(additions, [], [], fileContents);
    return changes.map((change) => {
      const oldContent = fileContents[change.path] || "";
      const diffLines = computeSimpleDiff(oldContent, change.content);
      return { path: change.path, diffLines };
    });
  }, [additions, fileContents]);

  return (
    <Modal
      title="Review Schema Changes"
      open
      onOk={onConfirm}
      onCancel={onCancel}
      okText="Confirm & Create PR"
      cancelText="Cancel"
      width={800}
    >
      {fileDiffs.map(({ path, diffLines }) => (
        <div key={path} style={{ marginBottom: 24 }}>
          <Text strong>
            <code>{path}</code>
          </Text>
          <pre
            style={{
              marginTop: 8,
              padding: 8,
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              fontSize: 12,
              lineHeight: "1.6",
              overflow: "auto",
              maxHeight: 400,
              backgroundColor: "#fafafa",
            }}
          >
            {diffLines.map((line) => (
              <div
                key={line.key}
                style={{
                  ...getDiffLineStyle(line.type),
                  paddingLeft: 4,
                  paddingRight: 4,
                  whiteSpace: "pre",
                  fontFamily: "monospace",
                }}
              >
                {getDiffLinePrefix(line.type)} {line.text}
              </div>
            ))}
          </pre>
        </div>
      ))}
    </Modal>
  );
}

export default SchemaDiffPreview;
